import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Search, BookOpen, Users, Calendar, FileText, ExternalLink, ChevronRight, Globe, Clock } from 'lucide-react';
import { getJournalById, getEditorialBoard, getJournalPapers } from '../services/api';
import { useAuth } from '../context/AuthContext';

const JournalDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [journal, setJournal] = useState(null);
  const [board, setBoard] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paperSearch, setPaperSearch] = useState('');
  const [activeSection, setActiveSection] = useState('about');

  useEffect(() => {
    getJournalById(id)
      .then(setJournal)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    getEditorialBoard(id).then(setBoard).catch(() => setBoard([]));
  }, [id]);

  useEffect(() => {
    getJournalPapers(id).then(setPapers).catch(() => setPapers([]));
  }, [id]);

  const filteredPapers = papers.filter(p =>
    p.title?.toLowerCase().includes(paperSearch.toLowerCase()) ||
    p.authorName?.toLowerCase().includes(paperSearch.toLowerCase()) ||
    p.keywords?.toLowerCase().includes(paperSearch.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-96 flex items-center justify-center bg-[#F5F7FA]">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[#6B7280]">Loading journal...</p>
      </div>
    </div>
  );

  if (notFound) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <AlertCircle size={40} className="text-[#9CA3AF] mx-auto mb-4" />
      <h2 className="font-serif text-2xl text-[#1F1F1F] mb-2">Journal Not Found</h2>
      <Link to="/journals" className="text-[#1A73E8] text-sm hover:underline">Back to Journals</Link>
    </div>
  );

  const sections = [
    { id: 'about', label: 'About' },
    { id: 'editorial', label: 'Editorial Board' },
    { id: 'papers', label: `Published Papers (${papers.length})` },
  ];

  return (
    <div className="bg-white min-h-screen" data-testid="journal-detail-page">

      {/* Breadcrumb */}
      <div className="bg-[#F5F7FA] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
            <Link to="/" className="hover:text-[#1A73E8] transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/journals" className="hover:text-[#1A73E8] transition-colors">Journals</Link>
            <ChevronRight size={12} />
            <span className="text-[#1F1F1F] font-medium">{journal?.shortTitle}</span>
          </div>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="bg-[#174EA6] border-b border-[#1A3A7A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <Link to="/journals" className="inline-flex items-center gap-1.5 text-blue-200 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft size={14} /> Back to Journals
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2.5 py-1 bg-white/10 text-blue-100 border border-white/20 font-medium tracking-wide">
                  {journal.field || 'Research Journal'}
                </span>
                <span className="text-xs px-2.5 py-1 bg-white/10 text-blue-100 border border-white/20 font-medium">
                  {journal.frequency || 'Quarterly'}
                </span>
              </div>
              <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-tight mb-2">
                {journal.title}
              </h1>
              <p className="text-blue-200 text-sm font-medium">{journal.shortTitle}</p>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => user ? navigate('/submit') : navigate('/login')}
                className="flex items-center gap-2 px-6 py-3 bg-white text-[#174EA6] text-sm font-semibold hover:bg-blue-50 transition-colors"
              >
                <FileText size={15} />
                Submit Paper
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            {[
              { icon: <FileText size={14} />, label: 'Published Papers', value: papers.length },
              { icon: <Users size={14} />, label: 'Editorial Members', value: board.length },
              { icon: <Clock size={14} />, label: 'Review Time', value: journal.reviewTime || '6–8 weeks' },
              { icon: <Globe size={14} />, label: 'ISSN', value: journal.issn || 'Pending' },
            ].map(stat => (
              <div key={stat.label} className="bg-white/10 border border-white/15 px-4 py-3">
                <div className="flex items-center gap-1.5 text-blue-200 mb-1">
                  {stat.icon}
                  <span className="text-xs">{stat.label}</span>
                </div>
                <p className="text-white font-semibold text-sm">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Nav */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-0 overflow-x-auto">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === s.id
                    ? 'border-[#1A73E8] text-[#1A73E8]'
                    : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-8">

          {/* MAIN CONTENT */}
          <div className="lg:col-span-2">

            {/* ABOUT SECTION */}
            {activeSection === 'about' && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-4 pb-2 border-b border-[#E5E7EB]">
                    About This Journal
                  </h2>
                  <p className="text-[#374151] text-sm leading-relaxed">
                    {journal.description || 'No description available.'}
                  </p>
                </div>

                <div>
                  <h2 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-4 pb-2 border-b border-[#E5E7EB]">
                    Aims & Scope
                  </h2>
                  <div className="text-[#374151] text-sm leading-relaxed whitespace-pre-line">
                    {journal.aimsAndScope || 'Not specified.'}
                  </div>
                </div>

                {journal.indexing && journal.indexing.length > 0 && (
                  <div>
                    <h2 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-4 pb-2 border-b border-[#E5E7EB]">
                      Indexing & Abstracting
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {journal.indexing.map((idx, i) => (
                        <span key={i} className="text-xs px-3 py-1.5 bg-[#EFF6FF] text-[#1A73E8] border border-[#BFDBFE] font-medium">
                          {idx}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Papers Preview */}
                {papers.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#E5E7EB]">
                      <h2 className="font-serif text-xl font-semibold text-[#1F1F1F]">Recent Publications</h2>
                      <button onClick={() => setActiveSection('papers')} className="text-xs text-[#1A73E8] hover:underline">
                        View all {papers.length} →
                      </button>
                    </div>
                    <div className="space-y-3">
                      {papers.slice(0, 3).map(p => (
                        <Link key={p.id} to={`/papers/${p.id}`}
                          className="block p-4 border border-[#E5E7EB] hover:border-[#1A73E8] hover:bg-[#F5F7FA] transition-all group">
                          <p className="text-sm font-semibold text-[#1F1F1F] group-hover:text-[#1A73E8] leading-snug mb-1.5 transition-colors">
                            {p.title}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-[#9CA3AF]">
                            <span>{p.authorName}</span>
                            {p.doi && <span className="text-green-600 font-mono">DOI: {p.doi}</span>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EDITORIAL BOARD SECTION */}
            {activeSection === 'editorial' && (
              <div>
                <h2 className="font-serif text-xl font-semibold text-[#1F1F1F] mb-6 pb-2 border-b border-[#E5E7EB]">
                  Editorial Board
                </h2>

                {/* Editor-in-Chief */}
                <div className="mb-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-3">Editor-in-Chief</p>
                  <div className="flex items-center gap-4 p-5 bg-[#EFF6FF] border border-[#BFDBFE]">
                    <div className="w-12 h-12 bg-[#1A73E8] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">
                        {(journal.editorInChief || 'E').charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#1F1F1F]">{journal.editorInChief || 'Not Assigned'}</p>
                      <p className="text-xs text-[#1A73E8] font-medium mt-0.5">Editor-in-Chief</p>
                    </div>
                  </div>
                </div>

                {/* Board Members */}
                {board.length === 0 ? (
                  <div className="p-8 text-center border border-[#E5E7EB] bg-[#F9FAFB]">
                    <Users size={28} className="text-[#D1D5DB] mx-auto mb-2" />
                    <p className="text-sm text-[#9CA3AF]">No editorial members assigned yet.</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-3">Editorial Members</p>
                    <div className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
                      {board.map((member, index) => (
                        <div key={member?.id || index} className="flex items-center gap-4 px-5 py-4 bg-white hover:bg-[#F9FAFB] transition-colors">
                          <div className="w-9 h-9 bg-[#174EA6] flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-bold">
                              {String(member?.name || member?.user?.name || 'E').charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1F1F1F]">
                              {member?.name || member?.user?.name || 'Editorial Member'}
                            </p>
                            <p className="text-xs text-[#6B7280] truncate">
                              {member?.institution || member?.affiliation || 'MAH Quantum Research Institute'}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 font-medium border flex-shrink-0 ${
                            member?.role === 'admin' || member?.category === 'editor'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : member?.role === 'reviewer' || member?.category === 'reviewer'
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>
                            {member?.role === 'admin' ? 'Editor' : member?.role === 'reviewer' ? 'Reviewer' : (member?.role || 'Member').toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Link to="/editorial-board" className="inline-flex items-center gap-1.5 text-sm text-[#1A73E8] hover:underline mt-4">
                      View full editorial board <ExternalLink size={12} />
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* PUBLISHED PAPERS SECTION */}
            {activeSection === 'papers' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-2 border-b border-[#E5E7EB]">
                  <h2 className="font-serif text-xl font-semibold text-[#1F1F1F]">
                    Published Papers
                    <span className="ml-2 text-sm font-normal text-[#6B7280]">({papers.length} total)</span>
                  </h2>
                </div>

                {/* Search */}
                <div className="relative mb-5">
                  <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    type="text"
                    value={paperSearch}
                    onChange={e => setPaperSearch(e.target.value)}
                    placeholder="Search papers by title, author or keyword..."
                    className="w-full pl-10 pr-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] bg-white"
                  />
                  {paperSearch && (
                    <button onClick={() => setPaperSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#374151] text-xs">
                      ✕
                    </button>
                  )}
                </div>

                {filteredPapers.length === 0 ? (
                  <div className="p-12 text-center border border-[#E5E7EB] bg-[#F9FAFB]">
                    <FileText size={32} className="text-[#D1D5DB] mx-auto mb-3" />
                    <p className="text-sm text-[#9CA3AF]">
                      {paperSearch ? `No papers matching "${paperSearch}"` : 'No published papers yet.'}
                    </p>
                    {paperSearch && (
                      <button onClick={() => setPaperSearch('')} className="mt-2 text-xs text-[#1A73E8] hover:underline">
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
                    {filteredPapers.map((p, i) => (
                      <Link
                        key={p.id}
                        to={`/papers/${p.id}`}
                        className="block px-5 py-4 bg-white hover:bg-[#F5F7FA] transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-xs text-[#9CA3AF] font-mono">#{i + 1}</span>
                              {p.doi && (
                                <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 font-medium">
                                  DOI
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-[#1F1F1F] group-hover:text-[#1A73E8] leading-snug transition-colors mb-1.5">
                              {p.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280]">
                              <span className="flex items-center gap-1">
                                <Users size={11} /> {p.authorName}
                              </span>
                              {p.doi && (
                                <span className="font-mono text-green-600">{p.doi}</span>
                              )}
                            </div>
                            {p.abstract && (
                              <p className="text-xs text-[#9CA3AF] mt-2 leading-relaxed line-clamp-2">
                                {p.abstract}
                              </p>
                            )}
                          </div>
                          <ChevronRight size={16} className="text-[#D1D5DB] group-hover:text-[#1A73E8] flex-shrink-0 mt-1 transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {paperSearch && filteredPapers.length > 0 && (
                  <p className="text-xs text-[#9CA3AF] mt-3 text-center">
                    Showing {filteredPapers.length} of {papers.length} papers
                  </p>
                )}
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="space-y-5">

            {/* Submit CTA */}
            <div className="bg-[#174EA6] p-5 text-white">
              <h3 className="font-semibold text-sm mb-1.5">Submit to This Journal</h3>
              <p className="text-blue-200 text-xs mb-4 leading-relaxed">
                Contribute your research to {journal.shortTitle}. All submissions undergo rigorous peer review.
              </p>
              <button
                onClick={() => user ? navigate('/submit') : navigate('/login')}
                className="w-full py-2.5 bg-white text-[#174EA6] text-sm font-semibold hover:bg-blue-50 transition-colors"
              >
                Submit Manuscript
              </button>
            </div>

            {/* Journal Info */}
            <div className="border border-[#E5E7EB]">
              <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Journal Details</h3>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { label: 'ISSN (Print)', value: journal.issn || 'Pending Assignment' },
                  { label: 'e-ISSN', value: journal.eIssn || 'Pending Assignment' },
                  { label: 'Field', value: journal.field || '—' },
                  { label: 'Frequency', value: journal.frequency || 'Quarterly' },
                  { label: 'Impact Factor', value: journal.impactFactor || 'Not Yet Rated' },
                  { label: 'Acceptance Rate', value: journal.acceptanceRate || 'Under Review' },
                  { label: 'Review Time', value: journal.reviewTime || '6–8 weeks' },
                  { label: 'Volume', value: `Vol. ${journal.currentVolume || 1}, Issue ${journal.currentIssue || 1}` },
                ].map(item => (
                  <div key={item.label} className="flex justify-between gap-2 text-xs border-b border-[#F5F7FA] pb-2 last:border-0 last:pb-0">
                    <span className="text-[#9CA3AF] flex-shrink-0">{item.label}</span>
                    <span className="text-[#374151] font-medium text-right">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Nav */}
            <div className="border border-[#E5E7EB]">
              <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Quick Navigation</h3>
              </div>
              <div className="divide-y divide-[#E5E7EB]">
                {sections.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full text-left px-4 py-3 text-sm flex items-center justify-between transition-colors ${
                      activeSection === s.id
                        ? 'bg-blue-50 text-[#1A73E8] font-medium'
                        : 'text-[#374151] hover:bg-[#F9FAFB]'
                    }`}
                  >
                    {s.label}
                    <ChevronRight size={14} className="text-[#D1D5DB]" />
                  </button>
                ))}
              </div>
            </div>

            {/* Submission Guidelines */}
            <div className="border border-[#E5E7EB] p-4 bg-[#F9FAFB]">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-3">Submission Notes</h3>
              <ul className="space-y-2">
                {[
                  'PDF format only, max 20MB',
                  'Double-blind peer review',
                  `Avg. decision in ${journal.reviewTime || '6–8 weeks'}`,
                  'DOI assigned on acceptance',
                ].map(note => (
                  <li key={note} className="flex items-start gap-2 text-xs text-[#6B7280]">
                    <span className="text-[#1A73E8] mt-0.5 flex-shrink-0">•</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalDetailPage;
